import React, { useEffect, useMemo, useRef, useState } from "react";
import { AlertTriangle } from "lucide-react";
import { toast } from "sonner";
import api, { friendlyMessage } from "../lib/api";
import { Button } from "./ui/button";
import {
  canConfirm, changedMessage, changeOutcome, confirmBody, currentChoiceValue, money, pendingMessage, previewBody, previewLines,
  productChoices, productLabel, refusal,
} from "../lib/subscriptionProduct";

/**
 * "Change product / size" inside an Edit subscription dialog (customer:
 * pages/customer/Subscriptions.jsx; admin: pages/admin/AdminCRUD.jsx). Render
 * it only when the store has the feature on (lib/subscriptionProduct
 * isProductChangeOn).
 *
 * It is its own step with its own confirmation — pick, check the price, read
 * what will change, confirm — and never touches the dialog's Save button or
 * what that button sends. The server prices and applies the change; this
 * shows its preview and sends back what was shown.
 *
 * A confirmed change that is complete closes the Edit dialog (the parent does
 * that in `onChanged` when the outcome is "done"), so nobody is left looking
 * at a Save button for a form they did not mean to save. Because of that:
 *   - `unsaved` (the dialog's own form has edits): the step is not offered —
 *     those edits must be saved or discarded first, never dropped silently;
 *   - a change that is saved but still being applied to some upcoming days
 *     ("pending") is NOT reported as a success and does not close anything:
 *     its status stays on screen until the person closes it (`onClose`);
 *   - a failed request changes nothing and keeps the step open;
 *   - an answer that arrives after this step has left the screen (the dialog
 *     was closed while the request was in flight — perhaps another
 *     subscription's dialog is open by now) is announced, and the parent is
 *     told it is `late`: it refreshes its list and touches no dialog. Render
 *     this with `key={sub.id}` so each subscription gets its own step.
 */
export default function SubscriptionProductChange({ sub, products, endpoint, admin = false, unsaved = false, onChanged, onClose }) {
  const choices = useMemo(() => productChoices(products), [products]);
  const [open, setOpen] = useState(false);
  const [value, setValue] = useState("");
  const [preview, setPreview] = useState(null);
  const [acknowledged, setAcknowledged] = useState(false);
  const [busy, setBusy] = useState(false);
  const [pending, setPending] = useState(null);     // a confirm that is saved but not finished
  // Still on screen? An answer can outlive the dialog it was asked from.
  const onScreen = useRef(true);
  useEffect(() => {
    onScreen.current = true;
    return () => { onScreen.current = false; };
  }, []);

  const current = currentChoiceValue(sub, choices);
  const body = previewBody(choices, value);
  const lines = previewLines(preview, { admin });

  const close = () => { setOpen(false); setValue(""); setPreview(null); setAcknowledged(false); };
  const pick = (v) => { setValue(v); setPreview(null); setAcknowledged(false); };

  const check = async () => {
    if (unsaved || !body) return;
    setBusy(true);
    try {
      const { data } = await api.post(endpoint, body);
      if (onScreen.current) setPreview(data);
    } catch (e) {
      if (!onScreen.current) return;
      setPreview(null);
      toast.error(refusal(e, friendlyMessage(e)).message);
    } finally {
      if (onScreen.current) setBusy(false);
    }
  };

  const confirm = async () => {
    if (unsaved || !body || !canConfirm(preview, { admin, acknowledged })) return;
    setBusy(true);
    try {
      const { data } = await api.post(endpoint, confirmBody(body, preview, { admin, acknowledged }));
      const outcome = changeOutcome(data);
      const late = !onScreen.current;       // the dialog this was asked from is gone
      if (outcome === "done") {
        toast.success(changedMessage(data));
        if (!late) close();
      } else if (outcome === "pending") {
        // Saved, but not everything is updated yet: say exactly that, and keep it on screen.
        toast.warning(pendingMessage(data));
        if (!late) { close(); setPending(data); }
      } else {
        toast.message(changedMessage(data));
        if (!late) setPreview(null);
      }
      if (onChanged) onChanged(data, outcome, { late });
    } catch (e) {
      const r = refusal(e, friendlyMessage(e));
      toast.error(r.message);
      if (!onScreen.current) return;
      if (r.preview) {
        // The price or the first date moved since the preview: show the new one.
        setPreview(r.preview); setAcknowledged(false);
      } else if (r.conflict) {
        // The subscription is no longer what this preview describes: ask again.
        setPreview(null); setAcknowledged(false);
      }
    } finally {
      if (onScreen.current) setBusy(false);
    }
  };

  return (
    <div data-testid="product-change">
      <label className="text-xs uppercase tracking-wider font-bold text-neutral-500">Product / pack size</label>
      {pending ? (
        <div className="mt-2 rounded-2xl bg-amber-50 border border-amber-300 p-3 space-y-3 text-xs text-amber-900" data-testid="product-change-pending">
          <div className="font-bold text-sm">{productLabel(sub)}</div>
          <div><AlertTriangle className="inline w-3.5 h-3.5 mr-1 -mt-0.5"/>{pendingMessage(pending)}</div>
          <Button type="button" variant="outline" onClick={() => onClose && onClose()}
                  className="w-full h-11 rounded-2xl font-bold" data-testid="product-change-pending-close">
            Close
          </Button>
        </div>
      ) : !open ? (
        <>
          <div className="mt-2 flex items-center gap-2 rounded-2xl border border-neutral-200 bg-white px-3 h-12">
            <div className="flex-1 min-w-0 truncate text-sm font-bold" data-testid="product-change-current">
              {productLabel(sub)}
            </div>
            <button type="button" onClick={() => setOpen(true)} disabled={unsaved}
                    className="text-sm font-bold text-brand-sky shrink-0 disabled:text-neutral-400 disabled:cursor-not-allowed"
                    data-testid="product-change-open">Change</button>
          </div>
          {unsaved && <UnsavedNotice/>}
        </>
      ) : (
        <div className="mt-2 rounded-2xl border border-neutral-200 bg-white p-3 space-y-3" data-testid="product-change-panel">
          <select value={value} onChange={(e) => pick(e.target.value)} disabled={busy}
                  className="h-12 w-full rounded-2xl border border-neutral-200 bg-white px-3 text-base"
                  data-testid="product-change-select">
            <option value="">Choose a product…</option>
            {choices.map((c) => (
              <option key={c.value} value={c.value} disabled={c.disabled || c.value === current}>
                {c.label}{c.value === current ? " (current)" : ` — ${money(c.price)}`}{c.why ? ` (${c.why})` : ""}
              </option>
            ))}
          </select>

          {lines && (
            <div className="rounded-2xl bg-brand-cream p-3 text-xs text-neutral-700 space-y-1.5" data-testid="product-change-preview">
              {lines.nothing ? (
                <div className="font-bold" data-testid="product-change-nothing">{lines.nothing}</div>
              ) : (
                <>
                  <div><span className="text-neutral-500">Now:</span> <span data-testid="product-change-from">{lines.from}</span></div>
                  <div className="font-bold text-sm text-neutral-900"><span className="text-neutral-500 font-normal text-xs">New:</span> <span data-testid="product-change-to">{lines.to}</span></div>
                  <div data-testid="product-change-price">{lines.price}</div>
                  {lines.discount && <div data-testid="product-change-discount">{lines.discount}</div>}
                  <div data-testid="product-change-per-delivery">{lines.perDelivery}</div>
                  <div className="font-bold" data-testid="product-change-when">{lines.when}</div>
                  {lines.locked && <div data-testid="product-change-locked">{lines.locked}</div>}
                  <div className="text-neutral-500">Days already delivered or billed are not changed. Nothing is charged now.</div>
                </>
              )}
            </div>
          )}

          {lines?.wallet && (
            <div className="rounded-2xl bg-red-50 border border-red-200 px-3 py-2 text-xs text-red-700 font-bold" data-testid="product-change-wallet">
              {lines.wallet}
            </div>
          )}

          {unsaved && <UnsavedNotice/>}

          {lines?.pastCutoff && (
            <label className="flex items-start gap-2 rounded-2xl bg-amber-50 border border-amber-300 px-3 py-2 text-xs text-amber-900"
                   data-testid="product-change-past-cutoff">
              <input type="checkbox" checked={acknowledged} onChange={(e) => setAcknowledged(e.target.checked)}
                     className="mt-0.5 h-4 w-4 shrink-0" data-testid="product-change-ack"/>
              <span><AlertTriangle className="inline w-3.5 h-3.5 mr-1 -mt-0.5"/>{lines.pastCutoff} Tick to confirm.</span>
            </label>
          )}

          <div className="flex gap-2">
            <Button type="button" variant="outline" onClick={close} disabled={busy}
                    className="flex-1 h-11 rounded-2xl font-bold" data-testid="product-change-cancel">
              Cancel
            </Button>
            {preview && !preview.no_change ? (
              <Button type="button" onClick={confirm} disabled={busy || unsaved || !canConfirm(preview, { admin, acknowledged })}
                      className="flex-1 h-11 rounded-2xl bg-brand-sky hover:bg-blue-700 font-bold text-white"
                      data-testid="product-change-confirm">
                Confirm change
              </Button>
            ) : (
              <Button type="button" onClick={check} disabled={busy || unsaved || !body}
                      className="flex-1 h-11 rounded-2xl bg-brand-sky hover:bg-blue-700 font-bold text-white"
                      data-testid="product-change-check">
                Check price
              </Button>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

/** Changing the product closes the Edit screen, so it waits for the form's own edits. */
function UnsavedNotice() {
  return (
    <div className="mt-2 rounded-2xl bg-amber-50 border border-amber-300 px-3 py-2 text-xs text-amber-900" data-testid="product-change-unsaved">
      You have unsaved changes on this screen. Save them, or close this screen to discard them, before changing the product.
    </div>
  );
}
